import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatFullDate, formatRelativeTime } from "@/lib/format";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import {
  useLicenseTimeline,
  type LicenseEvent,
  type LicenseEventType,
} from "@/pages/licenses/hooks/useLicenseTimeline";
import type { License } from "@/pages/licenses/hooks/useMyLicenses";

const EVENT_META: Record<LicenseEventType, { icon: string; chip: string; labelKey: string }> = {
  issued: { icon: "ri-key-2-line", chip: "bg-primary-100 text-primary-700", labelKey: "timeline.typeIssued" },
  renewed: { icon: "ri-refresh-line", chip: "bg-accent-100 text-accent-700", labelKey: "timeline.typeRenewed" },
  activated: { icon: "ri-computer-line", chip: "bg-secondary-100 text-secondary-900", labelKey: "timeline.typeActivated" },
  released: { icon: "ri-link-unlink", chip: "bg-background-200 text-foreground-700", labelKey: "timeline.typeReleased" },
};

const EVENT_FILTERS: (LicenseEventType | "all")[] = ["all", "issued", "renewed", "activated", "released"];

interface LicenseTimelineProps {
  licenses: License[];
}

// 客户侧许可证变更时间线：签发、续期与设备变更按时间倒序统一回放，便于追溯每一次授权变动。
export default function LicenseTimeline({ licenses }: LicenseTimelineProps) {
  const { t } = useTranslation();
  const { edition } = useLicenseLabels();
  const { events, loading, error, reload } = useLicenseTimeline(licenses);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState<LicenseEventType | "all">("all");

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: events.length, issued: 0, renewed: 0, activated: 0, released: 0 };
    events.forEach((event) => {
      map[event.type] = (map[event.type] ?? 0) + 1;
    });
    return map;
  }, [events]);

  const visibleEvents = useMemo(
    () => (filter === "all" ? events : events.filter((event) => event.type === filter)),
    [events, filter],
  );

  return (
    <div className="mb-6 rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
            <i className="ri-history-line" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("timeline.title")}</h2>
            <p className="text-[11px] text-foreground-500">{t("timeline.sub")}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-700">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-git-commit-line" />
            </span>
            {t("timeline.count", { count: events.length })}
          </span>
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </span>
            {expanded ? t("timeline.toggleHide") : t("timeline.toggleShow")}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 border-t border-background-200 pt-4">
          {events.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {EVENT_FILTERS.map((key) => {
                const active = filter === key;
                const label = key === "all" ? t("timeline.filterAll") : t(EVENT_META[key as LicenseEventType].labelKey);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    aria-pressed={active}
                    className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${
                      active
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {label}
                    <span
                      className={`rounded-full px-1.5 text-[10px] ${
                        active ? "bg-background-50/25 text-background-50" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {counts[key] ?? 0}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {loading ? (
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="h-8 w-8 rounded-md bg-background-100" />
                  <span className="h-3 flex-1 rounded bg-background-100" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 rounded-md border border-background-200 bg-background-100 py-8 text-center">
              <p className="text-xs text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("licenses.reload")}
              </button>
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-md border border-background-200 bg-background-100 py-8 text-center">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                <i className="ri-history-line text-lg" />
              </span>
              <p className="text-xs font-medium text-foreground-700">{t("timeline.empty")}</p>
              <p className="max-w-xs text-[11px] text-foreground-400">{t("timeline.emptyDesc")}</p>
            </div>
          ) : visibleEvents.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-md border border-background-200 bg-background-100 py-8 text-center">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                <i className="ri-filter-off-line text-lg" />
              </span>
              <p className="text-xs font-medium text-foreground-700">{t("timeline.filterNoMatch")}</p>
            </div>
          ) : (
            <ol className="flex flex-col gap-4">
              {visibleEvents.map((event: LicenseEvent, index) => {
                const meta = EVENT_META[event.type];
                return (
                  <li key={event.id} className="flex gap-3">
                    <span className="flex flex-col items-center">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${meta.chip}`}>
                        <i className={`${meta.icon} text-sm`} />
                      </span>
                      {index < visibleEvents.length - 1 && <span className="mt-1 w-px flex-1 bg-background-200" />}
                    </span>
                    <div className="min-w-0 flex-1 pb-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground-950">{t(meta.labelKey)}</span>
                        {event.productName && (
                          <span className="text-sm text-foreground-700">· {event.productName}</span>
                        )}
                        {event.edition && (
                          <span className="rounded bg-background-200 px-1.5 py-0.5 text-[10px] text-foreground-700">
                            {edition(event.edition)}
                          </span>
                        )}
                      </div>
                      {event.detail && <p className="mt-0.5 text-xs text-foreground-500">{event.detail}</p>}
                      <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-foreground-400">
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-time-line" />
                        </span>
                        {formatRelativeTime(event.at)} · {formatFullDate(event.at)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}